/* eslint-disable class-methods-use-this */
import React from 'react';
import { domToReact, DOMNode } from 'html-react-parser';
import { baseApiUrl } from 'data_sources/constants';
import { Link, Stack } from '@mui/material';
import PolicyMedia from 'views/App/Portal/Policy/shared/PolicyMedia';
import { AttachFile } from '@mui/icons-material';
import PolicyCrossref from 'views/App/Portal/Policy/shared/PolicyCrossref';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import ExcalidrawImage from 'views/App/Portal/Policy/shared/Excalidraw';
import { getIssuedManual } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { highlight } from '_lex/_foundations/phxColors';
import _ from 'lodash';
import AuthUtil from 'utils/AuthUtil';
export interface PolicyAttachment {
  sectionKey: string;
  section: string;
  attachmentKey: string;
  attachmentName: string;
}

export default class PolicyContentHelper {
  static parseStyleString(styleString: string): React.CSSProperties {
    if (!styleString) return {};
    return styleString.split(';').reduce((style: React.CSSProperties, rule: string) => {
      const [property, value] = rule.split(':').map((str) => str.trim());
      if (property && value) {
        // Convert CSS property names to camelCase
        const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        // Use type assertion to handle React's strict CSS property types
        (style as any)[camelProperty] = value;
      }
      return style;
    }, {});
  }

  static parseImageUrl(src: string): { manualId: string; attachmentId: string } {
    // Parse AWS S3 URL to extract manualId and attachmentId
    try {
      const s3UrlPattern = /manuals\/([^\/]+)\/attachments\/([^\/\?]+)/;
      const matches = src.match(s3UrlPattern);

      if (matches && matches.length === 3) {
        return {
          manualId: matches[1],
          attachmentId: matches[2],
        };
      }
    } catch (error) {
      console.error('Error parsing image URL:', error);
    }
    return {
      manualId: '',
      attachmentId: '',
    };
  }

  static handleRetrieveMedia = (key: string, onLoad: (content: string) => void) => {
    const token = AuthUtil.getToken();
    let scope = 'issued';
    const url = `${baseApiUrl()}/lexipub/manuals/${scope}/v1/media?key=${encodeURIComponent(key)}`;
    fetch(url, {
      method: 'GET',
      headers: {
        'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
        Authorization: token || '',
      },
    }).then(async (resp) => {
      const respData = await resp.json();
      if (respData.url) {
        fetch(respData.url, {
          method: 'GET',
        })
          .then((resp) => {
            return resp.blob();
          })
          .then((blob) => {
            const rdr = new FileReader();
            rdr.onload = (e) => onLoad(e.target?.result as string);
            rdr.readAsDataURL(blob);
          });
      }
    });
  };

  static handleRetrieveCrossref = (key: string, onLoad: (manual: Manual) => void) => {
    const parts = key.split('/');
    if (parts[0] === 'manuals') {
      const manualId = parts[1];
      getIssuedManual(manualId).then((manual) => onLoad(manual));
    } else {
      const orgId = parts[1];
      const scope = parts[3];
      const manualId = parts[4];
      const contentId = parts[6];

      getIssuedManual(manualId).then((manual) => onLoad(manual));
    }
  };

  static handleDownloadAttachment = (key: string, filename: string) => {
    const token = AuthUtil.getToken();
    let scope = 'issued';
    const url = `${baseApiUrl()}/lexipub/manuals/${scope}/v1/media?key=${encodeURIComponent(key)}`;

    fetch(url, {
      method: 'GET',
      headers: {
        'X-Api-Key': process.env.REACT_APP_API_GATEWAY_KEY || '',
        Authorization: token || '',
      },
    }).then(async (resp) => {
      const respData = await resp.json();
      if (respData.url) {
        fetch(respData.url, {
          method: 'GET',
        })
          .then((resp) => {
            return resp.blob();
          })
          .then((blob) => {
            const href = URL.createObjectURL(blob);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            URL.revokeObjectURL(href);
          });
      }
    });
  };

  /**
   * Checks whether an `<li>` is purely a wrapper for a nested list
   * (no visible text content of its own).
   */
  private static isNestedListWrapper(li: any): boolean {
    const children = li.children || [];
    const hasNestedList = children.some((c: any) => c.type === 'tag' && (c.name === 'ol' || c.name === 'ul'));
    if (!hasNestedList) return false;
    const hasOwnContent = children.some((c: any) => {
      if (c.type === 'text' && c.data?.trim()) return true;
      if (c.type === 'tag' && c.name !== 'ol' && c.name !== 'ul') {
        const innerText = (c.children || []).some((gc: any) => gc.type === 'text' && gc.data?.trim());
        return innerText;
      }
      return false;
    });
    return !hasOwnContent;
  }

  /**
   * Fixes `<ol>` numbering for old-format content where nested `<li><ol>` wrappers
   * cause the browser to reset or skip markers. Adds `value` on `<li>` and
   * `list-style: none` on pure nested wrappers.
   */
  private static handleOrderedList(node: any): React.ReactNode {
    const liChildren = (node.children || []).filter((child: any) => child.type === 'tag' && child.name === 'li');

    const alreadyHasValues = liChildren.every((li: any) => li.attribs?.value != null);
    if (alreadyHasValues) return undefined;

    let counter = 0;
    liChildren.forEach((li: any) => {
      const isWrapper = PolicyContentHelper.isNestedListWrapper(li);
      if (!isWrapper) {
        counter++;
      }
      li.attribs = { ...li.attribs, value: String(counter) };
      if (isWrapper) {
        const existingStyle = li.attribs.style || '';
        li.attribs.style = existingStyle ? `${existingStyle}; list-style: none` : 'list-style: none';
      }
    });

    return undefined;
  }

  static handleContent = (node: DOMNode, ind: number) => {
    if (node.type === 'tag') {
      if (node.name === 'ol') {
        PolicyContentHelper.handleOrderedList(node);
      }
      if (node.name === 'img') {
        let attachmentId = node.attribs['data-attachment-id'] || '';
        let manualId = node.attribs['data-manual-id'] || '';
        const src = node.attribs.src;

        // If data attributes are not provided, try to extract from S3 URL
        if (!attachmentId && src) {
          const parsedUrl = PolicyContentHelper.parseImageUrl(src);
          if (parsedUrl.attachmentId) {
            attachmentId = parsedUrl.attachmentId;
            manualId = parsedUrl.manualId;
          }
        }

        if (!attachmentId) {
          return null;
        }

        return (
          <PolicyMedia
            mediaKey={attachmentId}
            manualId={manualId}
            width={node.attribs.width}
            height={node.attribs.height}
            alt={node.attribs.title || node.attribs.alt}
          />
        );
      }
      if (node.name === 'a') {
        const href = node.attribs.href;
        /* if (node.attribs['data-attachment-id'] && (_.isEmpty(href) || !href.startsWith('s3://'))) {
          return <></>;
        } */
        if (!_.isEmpty(href) && href.startsWith('s3://')) {
          return (
            <Link
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault();
                PolicyContentHelper.handleDownloadAttachment(href.split('://')[1], node.attribs.title || 'file');
              }}
            >
              <Stack
                component='span'
                direction='row'
                spacing={2}
                sx={{ display: 'inline-flex' }}
              >
                <AttachFile />
                {domToReact(node.children as DOMNode[])}
              </Stack>
            </Link>
          );
        } else if (!_.isEmpty(href) && href.startsWith('xref://')) {
          const key = href.split('://')[1];

          return <PolicyCrossref xrefKey={key}>{domToReact(node.children as DOMNode[])}</PolicyCrossref>;
        } else {
          node.attribs.target = '_blank';
          node.attribs.rel = 'noopener noreferrer';
        }
      }
      if (node.attribs['data-lexical-excalidraw-json']) {
        const excalidrawJson = node.attribs['data-lexical-excalidraw-json'];

        return <ExcalidrawImage excalidrawJson={excalidrawJson} />;
      }
      if (node.name === 'span' && node.attribs['data-phx-cross-ref'] === 'true') {
        const contentUri = node.attribs['data-phx-cross-ref-content-uri'];
        if (!_.isEmpty(contentUri) && contentUri.startsWith('xref://')) {
          const key = contentUri.split('://')[1];
          return <PolicyCrossref xrefKey={key}>{domToReact(node.children as DOMNode[])}</PolicyCrossref>;
        }
      }
      if (['indexterm', 'primary', 'lexindex'].includes(node.name)) {
        return <></>;
      }
      if (['lexientity'].includes(node.name)) {
        return React.createElement(
          'span',
          {
            title: node.attribs.title,
            style: {
              fontSize: '16px',
              lineHeight: 1.5,
              fontWeight: 400,
              letterSpacing: '-0.48px',
              padding: '0 8px',
              borderRadius: '4px',
              color: highlight[900],
              border: `1px solid ${highlight[800]}`,
              display: 'inline-flex',
            },
          },
          domToReact(
            node.children.map((x) => x as DOMNode),
            { replace: PolicyContentHelper.handleContent },
          ),
        );
      }
    }
    return node;
  };

  static extractAttachments = (content: string) => {
    const contentNode = document.createElement('div');
    contentNode.innerHTML = content;
    const links: { key: string; name: string }[] = [];
    const linkMatches = contentNode.querySelectorAll('a');
    linkMatches.forEach((l) => {
      const href = l.getAttribute('href');
      if (!_.isEmpty(href) && href!.startsWith('s3://')) {
        links.push({
          key: href!.split('://')[1],
          name: l.title || '',
        });
      }
    });
    return links;
  };

  static formatLabel = (node: any) => {
    const parts = [];
    if (node?.heading) parts.push(node.heading);
    parts.push(node?.title?.value ?? '');
    return parts.join(': ');
  };
}
